import React, { useEffect, useState } from 'react';
import { Button, Steps, Spin } from 'antd';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import axios from 'axios';
import ENVIRONMENT from '../../../../../../environments';
import './EnterpriseCreditPurchase.scss';

const { Step } = Steps;

const EnterpriseCreditPurchase = ({
    creditPurchaseData,
    cardStatus,
    updateUserAccountInformation = () => {},
    loadUserRequestStatus,
    onClose,
    setSuccessModalVisible = () => {},
    setShowPaymentFailureModal = () => {},
    setSystemErrorModal = () => {}
}) => {
    const [stage, setStage] = useState(1);
    const [paymentInformation, setPaymentInformation] = useState(null);
    const [paymentLoader, setPaymentLoader] = useState(false);
    const [loadingCardInfo, setLoadingCardInfo] = useState(false);

    useEffect(() => {
        if (cardStatus === 1) {
            setLoadingCardInfo(true);
            let payload = {
                "username": localStorage.getItem('username'),
                "action": "getinfo"
            };
            axios.post(ENVIRONMENT.GET_USER_PAYMENT_INFORMATION, payload)
                .then(res => {
                    setPaymentInformation(res.data['card_information']);
                    setLoadingCardInfo(false);
                })
                .catch(err => {
                    console.error('Error loading payment info:', err);
                    setLoadingCardInfo(false);
                });
        }
    }, [cardStatus]);

    const initiateCheckout = () => {
        if (cardStatus === 1) {
            setStage(2);
        } else {
            // Open card save flow
            let child_window = window.open('/save-card?action=save_card', '', "width=600,height=600");
            
            window.addEventListener('card_saved', (e) => {
                let action = e.detail.action;
                if (action === 'save_card') {
                    loadUserRequestStatus();
                    setStage(2);
                }
            }, false);

            let refreshInterval = setInterval(() => {
                if (child_window.closed) {
                    clearInterval(refreshInterval);
                }
            }, 500);
        }
    };

    const changePaymentMethod = () => {
        let child_window = window.open('/save-card?action=save_card', '', "width=600,height=600");
        
        window.addEventListener('card_saved', (e) => {
            let action = e.detail.action;
            if (action === 'save_card') {
                // Delete previous card
                axios.post(ENVIRONMENT.GET_USER_PAYMENT_INFORMATION, {
                    'username': localStorage.getItem('username'),
                    'action': 'delete_previous'
                }).then((res) => {
                    updateUserAccountInformation(res.data);
                    loadUserRequestStatus();
                });
            }
        }, false);

        let refreshInterval = setInterval(() => {
            if (child_window.closed) {
                clearInterval(refreshInterval);
            }
        }, 500);
    };

    const handlePaymentAndCreditPurchase = () => {
        setPaymentLoader(true);
        
        // Transform creditPurchaseData.quantities into credit_details format
        const creditDetails = [];
        const { quantities } = creditPurchaseData;
        
        // Map of item IDs to their API request types and categories
        const itemMapping = {
            // Products
            'product_model': { request_type: 'product_model', category: '' },
            // 'complex_product_models': { request_type: 'complex_product_model', category: 'complex' },
            'simple_product_variants': { request_type: 'variation_product_model', category: 'simple' },
            // 'complex_product_variants': { request_type: 'complex_product_variant', category: 'complex' },
            'space_room_models': { request_type: 'room_prop', category: '' },
            
            // Images
            'ai_image_4k': { request_type: 'ai_image_4k', category: '' },
            'ai_final_images_4k_msp': { request_type: 'ai_image', category: '4k_msp' },
            'silo_images_1k': { request_type: 'product_silo', category: '1k' },
            'silo_images_2k': { request_type: 'product_silo', category: '2k' },
            'silo_images_4k': { request_type: 'product_silo', category: '4k' },
            'lifestyle_images_1k': { request_type: 'scene_lifestyle_image', category: '1k' },
            'lifestyle_images_2k': { request_type: 'scene_lifestyle_image', category: '2k' },
            'lifestyle_images_4k': { request_type: 'scene_lifestyle_image', category: '4k' },
            'lifestyle_images_4k_msp': { request_type: 'scene_lifestyle_image', category: '4k_msp' },
            'scene_lifestyle_360_1k': { request_type: 'scene_lifestyle_360', category: '1k' },
            'scene_lifestyle_360_2k': { request_type: 'scene_lifestyle_360', category: '2k' },
            'scene_lifestyle_360_4k': { request_type: 'scene_lifestyle_360', category: '4k' },
            // 'ai_image_1k': { request_type: 'ai_image', category: '' },
            
            // Other Assets
            '360_spins': { request_type: '360_spin', category: 'default' },
            'mp4_videos': { request_type: 'mp4_video', category: 'default' },
            '3d_tours': { request_type: '3d_tour', category: 'default' }
        };
        
        // Convert quantities to credit_details array
        Object.keys(quantities).forEach(itemId => {
            const quantity = quantities[itemId];
            if (quantity > 0 && itemMapping[itemId]) {
                const mapping = itemMapping[itemId];
                creditDetails.push({
                    request_type: mapping.request_type,
                    category: mapping.category,
                    quantity: quantity
                });
            }
        });
        
        let payload = {
            username: localStorage.getItem('username'),
            subscription_type: 'custom_contract',
            annual_subscription: false,
            credit_details: creditDetails
        };

        axios.post(ENVIRONMENT.UPGRADE_SUBSCRIPTION, payload).then(res => {
            setPaymentLoader(false);
            onClose();
            
            if (res.data.status_code === 400) {
                if (res.data.error_type === 'payment_failed')
                    setShowPaymentFailureModal(true);
                else
                    setSystemErrorModal(true);
            } else {
                loadUserRequestStatus();
                setSuccessModalVisible(true);
            }
        }).catch(err => {
            setPaymentLoader(false);
            setSystemErrorModal(true);
        });
    };

    const customDot = (dot, { status }) => {
        switch (status) {
            case "process":
                return <div className="custom-dot-process" />;
            case "finish":
                return (
                    <div className="custom-dot-finish">
                        <CheckOutlined className="check-icon" />
                    </div>
                );
            default:
                return dot;
        }
    };

    if (!creditPurchaseData) {
        return null;
    }

    const { totalCredits, totalPrice } = creditPurchaseData;

    return (
        <div style={{ width: '100%' }}>
            <div className="steps-tab-register">
                <Steps 
                    progressDot={customDot}
                    labelPlacement="vertical" 
                    className='step-class' 
                    style={{ marginBottom: 0, padding: '32px 48px' }} 
                    size="small" 
                    current={stage}>
                    <Step className="manrope f-14" title={<span className="manrope f-14 w-500 lh-22 black-085">Review Order</span>} />
                    <Step className="manrope f-14" title={<span className="manrope f-14 w-500 lh-22 black-085">Checkout</span>} />
                </Steps>
            </div>

            <div className="selected-plan-heading-gradient">
                <div className="manrope f-30 w-700 black-085 lh-32">
                    Enterprise Credit Purchase
                </div>
                <div className="manrope f-16 w-500 black-085 lh-24 mt-8">
                    Add credits to your enterprise account
                </div>
            </div>

            <div className="subscription-card" style={{ padding: '32px 25px', borderRadius: '4px' }}>
                {stage === 1 ? (
                    <>
                        <div className="price-section" style={{ marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                <span className="manrope f-26 w-700 lh-40 black-085">${totalPrice.toLocaleString()}</span>
                            </div>
                            <div className="manrope black-085 f-14 lh-28 w-400 gray-045">One-time purchase</div>
                        </div>

                        <div className="credit-system-bg" style={{ marginBottom: 24 }}>
                            <div className="manrope f-14 w-700 lh-22 black-085 text-left mb-8" style={{ display: 'flex', alignItems: 'center' }}>
                                <img src="/img/majesticons_coins-line.svg" className="mr-8" style={{ width: 20, height: 20 }} alt="credits" />
                                Credit Purchase Summary
                            </div>
                            <div className="manrope f-14 w-500 lh-22 black-085 text-left">
                                You are purchasing <b>{totalCredits} credits</b> for <b>${totalPrice.toLocaleString()}</b>. These credits can be used across various services including products, images, and other assets.
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', marginTop: 24 }}>
                            <Button className="back-btn" style={{ flex: '0 0 25%' }} onClick={onClose}>
                                Back
                            </Button>
                            <Button className="checkout-btn" style={{ flex: '0 0 calc(75% - 12px)' }} onClick={initiateCheckout}>
                                {cardStatus === 1 ? 'Continue to checkout' : 'Add Payment Method'}
                            </Button>
                        </div>

                        <div className="subscription-note" style={{ marginTop: 24, textAlign: 'center' }}>
                            This is a one-time purchase. Credits will be added to your account immediately after payment confirmation.
                        </div>
                    </>
                ) : (
                    // Payment Confirmation Stage
                    <>
                        {loadingCardInfo ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                            </div>
                        ) : cardStatus === 1 && paymentInformation ? (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                        <span className="manrope f-16 w-600 lh-24 black-085">
                                            {paymentInformation[0].card.brand.charAt(0).toUpperCase() + paymentInformation[0].card.brand.slice(1)}
                                        </span>
                                        <span className="manrope f-16 w-600 lh-24 black-085">
                                            ****{paymentInformation[0].card.last4}
                                        </span>
                                        <span className="manrope f-16 w-400 lh-24 black-085">
                                            Expires {String(paymentInformation[0].card.exp_month).padStart(2, "0")}/{paymentInformation[0].card.exp_year}
                                        </span>
                                    </div>
                                    <a onClick={changePaymentMethod} className="manrope f-16 w-400 lh-24 blue" style={{ cursor: 'pointer' }}>
                                        Change payment method
                                    </a>
                                </div>

                                <div style={{ display: 'flex', gap: '12px', marginTop: 24 }}>
                                    <Button onClick={() => setStage(1)} className="back-btn" style={{ flex: '0 0 25%' }}>
                                        Back
                                    </Button>
                                    <Button 
                                        onClick={handlePaymentAndCreditPurchase} 
                                        disabled={paymentLoader} 
                                        className="checkout-btn" 
                                        style={{ flex: '0 0 calc(75% - 12px)' }}>
                                        {paymentLoader ? <LoadingOutlined className="manrope f-14" /> : ''} Pay ${totalPrice.toLocaleString()}
                                    </Button>
                                </div>

                                <div className="subscription-note" style={{ marginTop: 24, textAlign: 'center' }}>
                                    This is a one-time purchase. Credits will be added to your account immediately after payment confirmation.
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default EnterpriseCreditPurchase;
